import i18n from "@shared/i18n/i18n";
import { Column, GroupData } from "./types";
import { IndexFiltersProps } from "@medentic/polaris-react";
import { t, TFunction } from "i18next";
import { formatNumber } from "@features/clinics/utils";
type Tone =
  | "base"
  | "inherit"
  | "subdued"
  | "caution"
  | "warning"
  | "critical"
  | "interactive"
  | "info"
  | "success"
  | "primary"
  | "emphasis"
  | "magic"
  | "textCaution"
  | "textWarning"
  | "textCritical"
  | "textInfo"
  | "textSuccess"
  | "textPrimary"
  | "textMagic";

const VALID_TONES: Tone[] = [
  "base",
  "inherit",
  "subdued",
  "caution",
  "warning",
  "critical",
  "interactive",
  "info",
  "success",
  "primary",
  "emphasis",
  "magic",
  "textCaution",
  "textWarning",
  "textCritical",
  "textInfo",
  "textSuccess",
  "textPrimary",
  "textMagic",
];

export function getTone(value: unknown): Tone | undefined {
  return VALID_TONES.includes(value as Tone) ? (value as Tone) : undefined;
}

export const getSortOptions = (
  t: TFunction,
  schemaData: GroupData[]
): IndexFiltersProps["sortOptions"] => {
  if (!schemaData.length) return [];

  const sortableProperties = schemaData[0]?.sortableProperties || [];
  const groups = schemaData[0]?.groups || [];
  const sortOptions: IndexFiltersProps["sortOptions"] = [];

  const getDirectionLabels = (type: string) => {
    // Numeric and boolean types
    if (["phoneNumber", "number", "checkbox"].includes(type)) {
      return {
        asc: t("common.ascending"),
        desc: t("common.descending"),
      };
    }
    // Date type
    if (type === "date") {
      return {
        asc: t("common.oldestToNewest"),
        desc: t("common.newestToOldest"),
      };
    }
    // Text and other types
    return {
      asc: t("common.AtoZ"),
      desc: t("common.ZtoA"),
    };
  };

  // Collect all sortable properties
  groups.forEach((g) => {
    g.properties
      .filter((prop) => sortableProperties.includes(prop.name))
      .forEach((prop) => {
        const directionLabels = getDirectionLabels(prop.renderer?.id ?? "");

        // Add ascending option
        sortOptions.push({
          label: prop.label,
          value: `${prop.name} asc`,
          directionLabel: directionLabels.asc,
        });

        // Add descending option
        sortOptions.push({
          label: prop.label,
          value: `${prop.name} desc`,
          directionLabel: directionLabels.desc,
        });
      });
  });

  // Sort options alphabetically by label
  return sortOptions.sort((a, b) => a.label.localeCompare(b.label));
};

export function getFilterLabel(
  filterKey: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterValue: any,
  dynamicSchemaFields: Column[],
  filterOptions?: { label: string; value: string }[]
): string {
  const field = dynamicSchemaFields.find((f) => f.name === filterKey);
  if (!field) return String(filterValue);

  if (
    field.id === "combobox" ||
    field.id === "combeBoxMultiple" ||
    field.id === "teamMembers" ||
    filterKey === "tags"
  ) {
    const options =
      (field.options || filterOptions)?.filter((opt) => {
        if (Array.isArray(filterValue)) {
          return filterValue.includes(opt.value);
        }
        return opt.value === filterValue;
      }) ?? [];
    return options.map((opt) => opt.label).join(", ");
  }
  if (field.id === "number") {
    if (filterValue.includes("|")) {
      const [minStr, maxStr] = filterValue.split("|");
      const min = Number(minStr);
      const max = Number(maxStr);
      if (isNaN(min) || min === 0) {
        return `≤${formatNumber(max, i18n.language)}`;
      }
      if (isNaN(max) || max === 0) {
        return `≥${formatNumber(min, i18n.language)}`;
      }
      return `${formatNumber(min, i18n.language)} … ${formatNumber(
        max,
        i18n.language
      )}`;
    }

    return formatNumber(Number(filterValue), i18n.language);
  }

  if (field.id === "checkbox") {
    if (filterValue === null) {
      return t("common.notSet");
    }
    return filterValue === "true" ? t("common.yes") : t("common.no");
  }

  return String(filterValue);
}

export const getSensitivityLabel = (level?: string): string => {
  if (!level) return "None";
  return level;
};

export const getSensitivityTooltip = (level?: string): string => {
  if (!level) return "Non-sensitive field";
  return `properties.sensitivity.${level.toLowerCase()}`;
};

export const getColumnDisplayOrder = (
  columnName: string,
  viewColumnsNames: string[],
  displayOrder: number
) => {
  return viewColumnsNames.includes(columnName)
    ? viewColumnsNames.indexOf(columnName)
    : displayOrder;
};

export const getMaxColumnDisplayOrder = (columns: Column[]): number => {
  return columns.reduce((prev: number, next: Column) => {
    return prev > (next.displayOrder ?? 0) ? prev : next?.displayOrder ?? 0;
  }, 0);
};
